/**
 * Time Clock — Phase 2: Timesheets View
 *
 * Weekly editable shifts panel for one employee at a time.
 *
 * - Admin / Manager: pick any employee, edit start/end/job, approve/reject,
 *   add manager notes, view audit log, decide PTO requests.
 * - Employee: locked to their own timesheet, can edit employee note only on
 *   their own pending entries, request PTO.
 *
 * Reads/writes `localStorage.maidly_timeEntries` and `localStorage.maidly_ptoRequests`.
 * AutoSync handles cloud sync transparently.
 */
import { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChevronLeft, ChevronRight, X,
  History, Paperclip, Plus, Calendar as CalendarIcon,
  Clock, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import {
  formatHM, formatDuration, liveWorkedMinutes,
  startOfWeek, weekDays, toISODate, computeWeeklyStats, auditEvent,
  diffEntry, normalizeEntry, newId,
} from '../../utils/timeClockHelpers';
import { getCrewName } from '../../services/CrewService';
import WeekStatsBar from './WeekStatsBar';
import PendingPtoBanner from './PendingPtoBanner';
import { exportEmployeeStatement } from './exportEmployeeStatement';
import { IconTile, TINT } from '../ui/IconTile';
import ShiftEditorRow, {
  ENTRY_TYPES,
  APPROVAL_STYLES,
  DayCell,
  td,
  editInput,
  smallBtn,
} from './ShiftEditorRow';

const TimesheetsView = ({ C, employees, entries, saveEntries, ptoRequests, savePtoRequests, preselectedEmployeeId }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isManager = user?.role === 'manager';
  const isEmployee = user?.role === 'employee';
  const canApprove = isAdmin || isManager;

  // Default selected: a preselected drill-in target (from the overview heat-map),
  // otherwise yourself (if you're an employee) or the first visible employee.
  const defaultEmpId = useMemo(() => {
    if (preselectedEmployeeId && employees.some((e) => e.id === preselectedEmployeeId)) {
      return preselectedEmployeeId;
    }
    if (isEmployee) {
      return employees.find((e) => e.email === user?.email || e.id === user?.id)?.id || '';
    }
    return employees[0]?.id || '';
  }, [isEmployee, employees, user, preselectedEmployeeId]);

  const [selectedEmpId, setSelectedEmpId] = useState(defaultEmpId);
  const [weekAnchor, setWeekAnchor] = useState(() => {
    // If the user drilled in from the Overview grid by clicking a specific day,
    // land the Detail view on that week instead of the current week.
    try {
      const iso = sessionStorage.getItem('timesheet_drill_week_anchor');
      if (iso) {
        sessionStorage.removeItem('timesheet_drill_week_anchor');
        return startOfWeek(new Date(iso));
      }
    } catch { /* ignore */ }
    return startOfWeek(new Date());
  });
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showPtoModal, setShowPtoModal] = useState(false);

  const selectedEmp = employees.find((e) => e.id === selectedEmpId);

  // Employees viewable in the dropdown (role-scoped)
  const viewableEmps = useMemo(() => {
    if (isEmployee) {
      return employees.filter((e) => e.email === user?.email || e.id === user?.id);
    }
    if (isManager) {
      const me = employees.find((e) => e.email === user?.email);
      if (me?.crewId) return employees.filter((e) => e.crewId === me.crewId);
    }
    return employees;
  }, [employees, isEmployee, isManager, user]);

  const week = useMemo(() => weekDays(weekAnchor), [weekAnchor]);
  const stats = useMemo(
    () => computeWeeklyStats(entries, selectedEmpId, weekAnchor),
    [entries, selectedEmpId, weekAnchor]
  );

  // Pending PTO requests for the selected employee (so admins can approve inline)
  const pendingPtoForEmp = useMemo(
    () => ptoRequests.filter((r) => r.employeeId === selectedEmpId && r.status === 'pending'),
    [ptoRequests, selectedEmpId]
  );

  // Entries grouped by date string for the week
  const entriesByDate = useMemo(() => {
    const m = {};
    week.forEach((d) => (m[toISODate(d)] = []));
    stats.entries.forEach((e) => {
      const k = toISODate(new Date(e.startTime));
      if (!m[k]) m[k] = [];
      m[k].push(e);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.startTime.localeCompare(b.startTime)));
    return m;
  }, [week, stats.entries]);

  // ---------- handlers ----------
  const startEdit = (entry) => {
    // Admins/managers can edit ANY entry (pending, approved, or rejected).
    // Employees can only edit their own pending entries — match on id OR email
    // since the AuthContext user.id may not always equal the maidly_employees
    // record id.
    const isOwnEntry =
      isEmployee &&
      entry.approvalStatus === 'pending' &&
      (entry.employeeId === user?.id ||
        employees.find((e) => e.id === entry.employeeId)?.email === user?.email);
    if (!canApprove && !isOwnEntry) {
      return toast.error('Only pending entries can be edited');
    }
    setEditingId(entry.id);
    // Split ISO into date + time-of-day so the editor shows two clean fields
    // instead of one confusing datetime-local blob.
    const splitIso = (iso) => {
      if (!iso) return { date: '', time: '' };
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return {
        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      };
    };
    const s = splitIso(entry.startTime);
    const e = splitIso(entry.endTime);
    setDraft({
      startDate: s.date,
      startTimeOnly: s.time,
      endDate: e.date || s.date,
      endTimeOnly: e.time,
      job: entry.job || '',
      employeeNote: entry.employeeNote || '',
      managerNote: entry.managerNote || '',
      type: entry.type || 'shift',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const saveEdit = (entry) => {
    const before = entry;
    // Recombine date + time-of-day pairs into local ISO strings.
    const joinLocal = (date, time) => {
      if (!date || !time) return null;
      const dt = new Date(`${date}T${time}`);
      return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
    };
    const startIso = joinLocal(draft.startDate, draft.startTimeOnly) || entry.startTime;
    const endIso = joinLocal(draft.endDate, draft.endTimeOnly);
    if (endIso && new Date(endIso) <= new Date(startIso)) {
      toast.error('End time must be after start time');
      return;
    }
    // Employees can only edit their own employeeNote
    const next = canApprove
      ? {
          ...entry,
          startTime: startIso,
          endTime: endIso,
          job: draft.job,
          employeeNote: draft.employeeNote,
          managerNote: draft.managerNote,
          type: draft.type,
        }
      : { ...entry, employeeNote: draft.employeeNote };

    // Recompute hours/pay
    if (next.endTime) {
      const mins = liveWorkedMinutes(next);
      next.hours = parseFloat((mins / 60).toFixed(2));
      next.pay = parseFloat((next.hours * (next.hourlyRate || 0)).toFixed(2));
    }
    const changes = diffEntry(before, next);
    if (changes) {
      next.auditLog = [...(entry.auditLog || []), auditEvent(user, 'edited', changes)];
    }
    saveEntries((prev) => prev.map((e) => (e.id === entry.id ? next : e)));
    cancelEdit();
    toast.success('Shift updated');
  };

  const decide = (entry, decision) => {
    if (!canApprove) return;
    const next = {
      ...entry,
      approvalStatus: decision,
      approvedBy: user?.email,
      approvedAt: new Date().toISOString(),
      auditLog: [...(entry.auditLog || []), auditEvent(user, decision)],
    };
    saveEntries((prev) => prev.map((e) => (e.id === entry.id ? next : e)));
    toast.success(`Shift ${decision}`);
  };

  const removeEntry = (entry) => {
    if (!canApprove) return;
    if (!window.confirm(`Delete this ${entry.type || 'shift'} entry?`)) return;
    saveEntries((prev) => prev.filter((e) => e.id !== entry.id));
    toast.success('Entry deleted');
  };

  // Attach a file (base64 in IndexedDB-equivalent localStorage — small files only)
  const onAttach = (entry, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Max 2 MB per attachment');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const att = {
        id: newId('att'),
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl: ev.target.result,
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.email,
      };
      const next = {
        ...entry,
        attachments: [...(entry.attachments || []), att],
        auditLog: [...(entry.auditLog || []), auditEvent(user, 'attached', { name: [null, file.name] })],
      };
      saveEntries((prev) => prev.map((e) => (e.id === entry.id ? next : e)));
      toast.success('Attachment added');
    };
    reader.readAsDataURL(file);
  };

  const removeAttachment = (entry, attId) => {
    const next = {
      ...entry,
      attachments: (entry.attachments || []).filter((a) => a.id !== attId),
      auditLog: [...(entry.auditLog || []), auditEvent(user, 'removed-attachment')],
    };
    saveEntries((prev) => prev.map((e) => (e.id === entry.id ? next : e)));
  };

  // PTO request flow
  const submitPtoRequest = (payload) => {
    const emp = employees.find((e) => e.id === payload.employeeId);
    if (!emp) return toast.error('Employee not found');
    const req = {
      id: newId('pto'),
      employeeId: emp.id,
      employeeName: emp.name,
      startDate: payload.startDate,
      endDate: payload.endDate,
      type: payload.type,
      reason: payload.reason,
      status: 'pending',
      requestedAt: new Date().toISOString(),
      requestedBy: user?.email,
      decidedBy: null,
      decidedAt: null,
    };
    savePtoRequests((prev) => [...prev, req]);
    setShowPtoModal(false);
    toast.success('PTO request submitted');
  };

  // Approve / reject a PTO request. Approval auto-creates entries.
  const decidePto = (req, decision) => {
    if (!canApprove) return;
    savePtoRequests((prev) =>
      prev.map((r) =>
        r.id === req.id
          ? { ...r, status: decision, decidedBy: user?.email, decidedAt: new Date().toISOString() }
          : r
      )
    );
    if (decision === 'approved') {
      // Materialize each day between startDate and endDate as an 8h PTO entry
      const start = new Date(req.startDate);
      const end = new Date(req.endDate);
      const emp = employees.find((e) => e.id === req.employeeId);
      const rate = parseFloat(emp?.hourlyRate) || 0;
      const created = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const startIso = new Date(`${toISODate(d)}T09:00:00`).toISOString();
        const endIso = new Date(`${toISODate(d)}T17:00:00`).toISOString();
        created.push(
          normalizeEntry({
            id: newId(),
            employeeId: req.employeeId,
            employeeName: req.employeeName,
            startTime: startIso,
            endTime: endIso,
            type: req.type === 'sick' ? 'sick' : 'pto',
            hours: 8,
            pay: 8 * rate,
            hourlyRate: rate,
            approvalStatus: 'approved',
            approvedBy: user?.email,
            approvedAt: new Date().toISOString(),
            job: req.type === 'sick' ? 'Sick Leave' : 'Paid Time Off',
            auditLog: [auditEvent(user, 'created', { from: [null, `PTO request ${req.id}`] })],
          })
        );
      }
      saveEntries((prev) => [...prev, ...created]);
    }
    toast.success(`PTO ${decision}`);
  };

  // Add a brand-new shift for a day where the employee never clocked in.
  // Defaults to 09:00–17:00 so the manager can save immediately and tweak
  // start/end via the row's edit button if needed.
  const addBlankShift = (day) => {
    if (!canApprove) {
      toast.error('Only managers can add shifts');
      return;
    }
    if (!selectedEmpId || !selectedEmp) {
      toast.error('Pick an employee first');
      return;
    }
    const dayStr = toISODate(day);
    const startIso = new Date(`${dayStr}T09:00:00`).toISOString();
    const endIso = new Date(`${dayStr}T17:00:00`).toISOString();
    const rate = parseFloat(selectedEmp.hourlyRate) || 0;
    const entry = normalizeEntry({
      id: newId(),
      employeeId: selectedEmpId,
      employeeName: selectedEmp.name,
      startTime: startIso,
      endTime: endIso,
      type: 'shift',
      hours: 8,
      pay: 8 * rate,
      hourlyRate: rate,
      job: '',
      approvalStatus: 'approved',
      approvedBy: user?.email,
      approvedAt: new Date().toISOString(),
      auditLog: [auditEvent(user, 'created', { reason: [null, 'Manual entry added by manager'] })],
    });
    saveEntries((prev) => [...prev, entry]);
    setEditingId(entry.id);
    setDraft({
      type: 'shift',
      job: '',
      startTime: startIso.slice(0, 16),
      endTime: endIso.slice(0, 16),
      employeeNote: '',
      managerNote: '',
    });
    toast.success(`Added shift for ${day.toLocaleDateString()}`);
  };

  // ---------- render ----------
  return (
    <div data-testid="timesheets-view">
      <style>{`
        @keyframes timeclock-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.55; transform: scale(1.35); }
        }
        [data-testid="timesheet-table"] tbody tr:hover {
          background: rgba(0,115,234,0.05) !important;
        }
      `}</style>
      {/* ===== Toolbar ===== */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '0.85rem',
          flexWrap: 'wrap', marginBottom: '1.25rem',
          position: 'relative', zIndex: 10001,
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 14, padding: '0.85rem 1rem',
        }}
      >
        {/* Employee selector with label */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label
            htmlFor="timesheet-employee-select"
            style={{ fontSize: '0.68rem', color: C.subtext, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            Employee
          </label>
          <select
            id="timesheet-employee-select"
            value={selectedEmpId}
            onChange={(e) => { setSelectedEmpId(e.target.value); setEditingId(null); }}
            data-testid="timesheet-employee-select"
            style={{
              background: C.surface, color: C.text, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: '0.6rem 0.85rem', fontSize: '0.9rem',
              fontWeight: 600, minWidth: 260, cursor: 'pointer',
            }}
          >
            <option value="">Select employee...</option>
            {viewableEmps.map((e) => (
              <option key={e.id} value={e.id}>{e.name}{e.crewId ? ` — ${getCrewName(e.crewId) || ''}` : ''}</option>
            ))}
          </select>
        </div>

        {/* Week navigator */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label
            style={{ fontSize: '0.68rem', color: C.subtext, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            Week
          </label>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
              padding: '0.3rem 0.4rem',
            }}
          >
            <button
              onClick={() => setWeekAnchor((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}
              data-testid="prev-week-button"
              style={{ ...iconBtn(C), padding: '0.4rem' }}
              aria-label="Previous week"
            >
              <ChevronLeft size={18} />
            </button>
            <div style={{ minWidth: 180, textAlign: 'center', fontSize: '0.9rem', fontWeight: 700, color: C.text }}>
              {week[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} —{' '}
              {week[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </div>
            <button
              onClick={() => setWeekAnchor((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}
              data-testid="next-week-button"
              style={{ ...iconBtn(C), padding: '0.4rem' }}
              aria-label="Next week"
            >
              <ChevronRight size={18} />
            </button>
            <button
              onClick={() => setWeekAnchor(startOfWeek(new Date()))}
              style={{
                background: '#0073ea', color: '#fff', border: 'none',
                borderRadius: 8, padding: '0.4rem 0.9rem',
                fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                marginLeft: 4, letterSpacing: 0.1,
                boxShadow: '0 2px 6px rgba(0,115,234,0.28)',
              }}
            >
              Today
            </button>
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <button
          onClick={() => {
            if (!selectedEmp) {
              toast.error('Please select an employee first');
              return;
            }
            const periodEnd = new Date(week[6]);
            periodEnd.setHours(23, 59, 59, 999);
            exportEmployeeStatement(selectedEmp, entries, week[0], periodEnd);
          }}
          data-testid="export-timesheet-pdf"
          disabled={!selectedEmp}
          title={selectedEmp ? `Export ${selectedEmp.name}'s timesheet as PDF` : 'Select an employee to export'}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            padding: '0.55rem 1rem',
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            cursor: selectedEmp ? 'pointer' : 'not-allowed',
            fontSize: '0.9rem', fontWeight: 600, color: selectedEmp ? '#374151' : '#94a3b8',
            transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            alignSelf: 'flex-end',
            opacity: selectedEmp ? 1 : 0.6,
          }}
          onMouseOver={(e) => {
            if (!selectedEmp) return;
            e.currentTarget.style.background = '#3b82f60d';
            e.currentTarget.style.borderColor = '#3b82f666';
            e.currentTarget.style.color = TINT.timeClock;
            e.currentTarget.style.boxShadow = '0 6px 16px #3b82f633';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.borderColor = C.border;
            e.currentTarget.style.color = selectedEmp ? '#374151' : '#94a3b8';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <IconTile icon={FileText} tint={TINT.timeClock} size={30} />
          Export PDF
        </button>

        <button
          onClick={() => setShowPtoModal(true)}
          data-testid="request-pto-button"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            padding: '0.55rem 1rem 0.55rem 0.55rem',
            background: TINT.green, color: '#fff', border: 'none', borderRadius: 12,
            fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            boxShadow: `0 6px 16px ${TINT.green}55`,
            transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            alignSelf: 'flex-end', letterSpacing: 0.1,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#059669';
            e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.green}88`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = TINT.green;
            e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.green}55`;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <IconTile icon={CalendarIcon} tint="rgba(255,255,255,0.22)" size={30} />
          Request PTO
        </button>
      </div>

      {!selectedEmp ? (
        <div
          style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
            padding: '3rem 1rem', textAlign: 'center', color: C.subtext,
          }}
        >
          Pick an employee above to view their weekly timesheet.
        </div>
      ) : (
        <>
          {/* ===== Stats banner ===== */}
          <WeekStatsBar C={C} stats={stats} />

          {/* ===== Pending PTO requests (admin/manager only) ===== */}
          {canApprove && (
            <PendingPtoBanner
              requests={pendingPtoForEmp}
              onApprove={(r) => decidePto(r, 'approved')}
              onReject={(r) => decidePto(r, 'rejected')}
            />
          )}

          {/* ===== Weekly table ===== */}
          <div
            style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
              overflow: 'hidden',
            }}
            data-testid="timesheet-table"
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: C.surfaceAlt, color: C.subtext, textAlign: 'left' }}>
                    <th style={th()}>Date</th>
                    <th style={th()}>Type</th>
                    <th style={th()}>Job</th>
                    <th style={th()}>Start</th>
                    <th style={th()}>End</th>
                    <th style={th()}>Total</th>
                    <th style={th()}>Notes</th>
                    <th style={th()}>Files</th>
                    <th style={th()}>Status</th>
                    <th style={th(true)}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {week.map((day, dayIdx) => {
                    const k = toISODate(day);
                    const dayEntries = entriesByDate[k] || [];
                    const dow = day.getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    const isToday = k === toISODate(new Date());
                    const zebraBg = dayIdx % 2 === 1 ? 'rgba(148,163,184,0.04)' : 'transparent';
                    const rowBg = isWeekend ? 'rgba(148,163,184,0.08)' : zebraBg;
                    const dayName = day.toLocaleDateString(undefined, { weekday: 'short' });
                    const dayDate = day.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
                    if (dayEntries.length === 0) {
                      return (
                        <tr key={k} style={{ borderTop: `1px solid ${C.border}`, background: rowBg }}>
                          <td style={td()}>
                            <DayCell dayName={dayName} dayDate={dayDate} isToday={isToday} isWeekend={isWeekend} C={C} />
                          </td>
                          <td colSpan={9} style={{ ...td(), color: C.subtext }}>
                            {canApprove ? (
                              <button
                                type="button"
                                data-testid={`timesheet-add-shift-${k}`}
                                onClick={() => addBlankShift(day)}
                                style={{
                                  background: 'transparent', border: `1px dashed ${C.border}`,
                                  color: C.primary, padding: '6px 12px', borderRadius: 8,
                                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                  display: 'inline-flex', alignItems: 'center', gap: 6,
                                }}
                              >
                                <Plus size={14} /> Add shift
                              </button>
                            ) : (
                              <span style={{ fontStyle: 'italic' }}>No entries</span>
                            )}
                          </td>
                        </tr>
                      );
                    }
                    return dayEntries.map((entry, idx) => {
                      const isEditing = editingId === entry.id;
                      const isExpanded = expandedId === entry.id;
                      return (
                        <ShiftEditorRow
                          key={entry.id}
                          entry={entry}
                          idx={idx}
                          dayName={dayName}
                          dayDate={dayDate}
                          isToday={isToday}
                          isWeekend={isWeekend}
                          isEditing={isEditing}
                          isExpanded={isExpanded}
                          draft={draft}
                          setDraft={setDraft}
                          canApprove={canApprove}
                          rowBg={rowBg}
                          C={C}
                          onStartEdit={() => startEdit(entry)}
                          onCancelEdit={cancelEdit}
                          onSaveEdit={() => saveEdit(entry)}
                          onDecide={(status) => decide(entry, status)}
                          onRemove={() => removeEntry(entry)}
                          onToggleExpand={() => setExpandedId(isExpanded ? null : entry.id)}
                        />
                      );
                    });
                  })}
                </tbody>
              </table>

              {/* Expanded row: attachments + audit log */}
              {expandedId && (() => {
                const entry = entries.find((e) => e.id === expandedId);
                if (!entry) return null;
                return (
                  <div
                    data-testid={`expanded-${entry.id}`}
                    style={{
                      borderTop: `1px solid ${C.border}`,
                      padding: '1rem 1.25rem',
                      background: C.surfaceAlt,
                      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem',
                    }}
                  >
                    {/* Attachments */}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Paperclip size={14} /> Attachments
                      </div>
                      {(entry.attachments || []).length === 0 && (
                        <div style={{ color: C.subtext, fontSize: '0.8rem', marginBottom: 8 }}>No attachments yet.</div>
                      )}
                      {(entry.attachments || []).map((a) => (
                        <div
                          key={a.id}
                          data-testid={`attachment-${a.id}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem',
                            padding: '0.35rem 0', borderTop: `1px dashed ${C.border}`,
                          }}
                        >
                          <a href={a.dataUrl} download={a.name} style={{ color: C.primary, textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {a.name}
                          </a>
                          <span style={{ color: C.subtext, fontSize: '0.7rem' }}>
                            {(a.size / 1024).toFixed(0)} KB
                          </span>
                          {canApprove && (
                            <button
                              onClick={() => removeAttachment(entry, a.id)}
                              data-testid={`remove-attachment-${a.id}`}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      ))}
                      <label
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          marginTop: 8, padding: '0.4rem 0.75rem', borderRadius: 8,
                          border: `1px dashed ${C.border}`, color: C.text, fontSize: '0.8rem',
                          cursor: 'pointer',
                        }}
                      >
                        <Plus size={14} /> Add file
                        <input
                          type="file"
                          data-testid={`attach-input-${entry.id}`}
                          onChange={(e) => onAttach(entry, e.target.files?.[0])}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>

                    {/* Audit log */}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <History size={14} /> Audit log
                      </div>
                      {(entry.auditLog || []).length === 0 ? (
                        <div style={{ color: C.subtext, fontSize: '0.8rem' }}>No activity recorded.</div>
                      ) : (
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                          {[...entry.auditLog].reverse().map((ev) => (
                            <li
                              key={ev.id || `${ev.timestamp || ''}-${ev.event || ''}`}
                              style={{
                                padding: '0.35rem 0',
                                borderTop: `1px dashed ${C.border}`,
                                fontSize: '0.78rem',
                                color: C.text,
                              }}
                            >
                              <strong>{ev.byName || ev.by}</strong>{' '}
                              <span style={{ color: C.primary }}>{ev.action}</span>{' '}
                              <span style={{ color: C.subtext }}>
                                — {new Date(ev.at).toLocaleString()}
                              </span>
                              {ev.changes && (
                                <div style={{ marginLeft: 12, color: C.subtext, fontSize: '0.72rem' }}>
                                  {Object.entries(ev.changes).map(([k, [a, b]]) => (
                                    <div key={k}>• {k}: {String(a) || '∅'} → {String(b) || '∅'}</div>
                                  ))}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {showPtoModal && (
        <PtoRequestModal
          C={C}
          employees={viewableEmps}
          defaultEmployeeId={selectedEmpId}
          onClose={() => setShowPtoModal(false)}
          onSubmit={submitPtoRequest}
        />
      )}
    </div>
  );
};

// ---------- small helpers / sub-components ----------
// DayCell, td, editInput, smallBtn are now imported from ShiftEditorRow.jsx
// (see top of file). Kept here: `th` (table header style) and `iconBtn`
// (used by non-row parts of this view) and `Stat`.

const th = (last = false) => ({
  padding: '0.7rem 0.9rem',
  fontWeight: 600, fontSize: '0.72rem',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  textAlign: last ? 'right' : 'left',
  whiteSpace: 'nowrap',
});
const iconBtn = (C) => ({
  background: 'transparent', border: 'none', cursor: 'pointer', color: C.text,
  padding: '0.3rem', borderRadius: 6, display: 'inline-flex', alignItems: 'center',
});

const Stat = ({ C, label, value, accent }) => (
  <div style={{ textAlign: 'left' }}>
    <div style={{ fontSize: '0.7rem', color: C.subtext, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: accent ? C.primary : C.text }}>
      {value}
    </div>
  </div>
);

const PtoRequestModal = ({ C, employees, defaultEmployeeId, onClose, onSubmit }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    employeeId: defaultEmployeeId || '', startDate: today, endDate: today, type: 'pto', reason: '',
  });
  return (
    <div
      onClick={onClose}
      data-testid="pto-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10010, padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface, color: C.text, borderRadius: 14,
          padding: '1.25rem 1.5rem', width: '100%', maxWidth: 460,
          border: `1px solid ${C.border}`,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Request PTO</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.text }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: '0.7rem', color: C.subtext, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Employee
            </div>
            <select
              value={form.employeeId}
              onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
              data-testid="pto-employee-select"
              style={{
                width: '100%', padding: '0.55rem 0.7rem',
                background: C.surface, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 8, fontSize: '0.85rem',
              }}
            >
              <option value="">Select...</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: '0.7rem', color: C.subtext, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Start
              </div>
              <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                style={{ width: '100%', padding: '0.55rem 0.7rem', background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: '0.85rem' }} />
            </label>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: '0.7rem', color: C.subtext, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                End
              </div>
              <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                style={{ width: '100%', padding: '0.55rem 0.7rem', background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: '0.85rem' }} />
            </label>
          </div>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: '0.7rem', color: C.subtext, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Type
            </div>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              data-testid="pto-type-select"
              style={{ width: '100%', padding: '0.55rem 0.7rem', background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: '0.85rem' }}>
              <option value="pto">Paid Time Off</option>
              <option value="sick">Sick Day</option>
              <option value="personal">Personal Day</option>
            </select>
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: '0.7rem', color: C.subtext, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Reason (optional)
            </div>
            <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2}
              style={{ width: '100%', padding: '0.55rem 0.7rem', background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: '0.85rem', resize: 'vertical' }} />
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1rem' }}>
          <button onClick={onClose} style={{ padding: '0.55rem 1rem', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.text, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
            Cancel
          </button>
          <button
            onClick={() => {
              if (!form.employeeId) return toast.error('Pick an employee');
              if (!form.startDate || !form.endDate) return toast.error('Dates required');
              if (new Date(form.endDate) < new Date(form.startDate)) return toast.error('End date must be ≥ start');
              onSubmit(form);
            }}
            data-testid="submit-pto-button"
            style={{ padding: '0.55rem 1rem', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
          >
            Submit Request
          </button>
        </div>
        <div style={{ marginTop: 6, color: C.subtext, fontSize: '0.72rem' }}>
          {`Days: ${Math.max(1, Math.round((new Date(form.endDate) - new Date(form.startDate)) / 86400000) + 1)}`}
        </div>
      </div>
    </div>
  );
};

export default TimesheetsView;
